import React, { useState } from "react";
import { Link } from "react-router-dom";
import Logo from "./Logo";
import { Instagram, Linkedin, Facebook, Youtube, MapPin, Phone, Mail } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("Welcome — your subscription is confirmed.");
      setEmail("");
    } catch (err) {
      toast.error("Please enter a valid email.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-[#0A0A0A] border-t border-[var(--twr-line)] text-[var(--twr-warm-white)]">
      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16 pt-20 pb-10">
        {/* Newsletter */}
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-20 items-end pb-16 border-b border-[var(--twr-line)]">
          <div>
            <p className="eyebrow">The Dispatch</p>
            <h3 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-4 leading-tight">
              An invitation to the inner circle.
            </h3>
            <p className="text-[var(--twr-warm-white)]/65 mt-4 max-w-md text-sm leading-relaxed">
              Private tasting evenings, releases, and member-only events — delivered with the discretion you'd expect.
            </p>
          </div>
          <form data-testid="newsletter-form" onSubmit={subscribe} className="flex flex-col sm:flex-row gap-3 w-full">
            <input
              data-testid="newsletter-email-input"
              type="email" required
              placeholder="your@email.com"
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="flex-1 bg-transparent border border-[var(--twr-line-strong)] px-5 py-4 text-sm tracking-wide focus:border-[var(--twr-bronze)] outline-none transition-colors"
            />
            <button data-testid="newsletter-submit" type="submit" disabled={loading} className="btn-bronze">
              {loading ? "Subscribing…" : "Subscribe"}
            </button>
          </form>
        </div>

        {/* Columns */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-10 pt-16">
          <div className="col-span-2 md:col-span-2 lg:col-span-2">
            <Logo variant="vertical" />
            <p className="text-[var(--twr-warm-white)]/55 text-sm mt-8 max-w-sm leading-relaxed">
              The Win Room is a private members club, restaurant, and cigar lounge in Lake Nona — built for those writing their own legacy.
            </p>
            <div className="flex items-center gap-3 mt-8">
              {[
                { Icon: Instagram, label: "Instagram", href: "https://www.instagram.com/thewinroomusa/" },
                { Icon: Linkedin, label: "LinkedIn", href: "https://www.linkedin.com/company/thewinroomusa/" },
                { Icon: Facebook, label: "Facebook", href: "https://www.facebook.com/profile.php?id=61591356159745" },
                { Icon: Youtube, label: "YouTube", href: "https://youtube.com/@thewinroomusa" },
              ].map(({ Icon, label, href }) => (
                <a key={label} href={href} aria-label={label} target="_blank" rel="noopener noreferrer"
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className="inline-flex items-center justify-center w-10 h-10 border border-[var(--twr-line-strong)] hover:border-[var(--twr-bronze)] hover:text-[var(--twr-bronze)] transition-colors"
                >
                  <Icon size={15} strokeWidth={1.4} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <p className="eyebrow mb-5">Explore</p>
            <ul className="space-y-3">
              {[
                { to: "/membership", l: "Membership" },
                { to: "/restaurant", l: "Restaurant" },
                { to: "/bar", l: "Bar" },
                { to: "/cigar-lounge", l: "Cigar Lounge" },
                { to: "/events", l: "Events" },
              ].map(({ to, l }) => (
                <li key={to}><Link to={to} className="footer-link">{l}</Link></li>
              ))}
            </ul>
          </div>

          <div>
            <p className="eyebrow mb-5">Visit</p>
            <ul className="space-y-3">
              {[
                { to: "/reservations", l: "Reservations" },
                { to: "/private-spaces", l: "Private Spaces" },
                { to: "/shop", l: "Shop" },
                { to: "/gallery", l: "Gallery" },
                { to: "/contact", l: "Contact" },
              ].map(({ to, l }) => (
                <li key={to}><Link to={to} className="footer-link">{l}</Link></li>
              ))}
            </ul>
          </div>

          <div>
            <p className="eyebrow mb-5">Lake Nona</p>
            <ul className="space-y-4 text-[13px] text-[var(--twr-warm-white)]/65">
              <li className="flex items-start gap-3">
                <MapPin size={14} strokeWidth={1.5} className="mt-0.5 text-[var(--twr-bronze)] shrink-0" />
                <span>9000 Tavistock Lakes Blvd<br/>Lake Nona, Orlando, FL 32827</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={14} strokeWidth={1.5} className="text-[var(--twr-bronze)] shrink-0" />
                <a href="tel:+14075550100" className="hover:text-[var(--twr-bronze)] transition-colors">(407) 555-0100</a>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={14} strokeWidth={1.5} className="text-[var(--twr-bronze)] shrink-0" />
                <a href="mailto:concierge@thewinroomfl.com" className="hover:text-[var(--twr-bronze)] transition-colors">concierge@thewinroomfl.com</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-[var(--twr-line)] flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-[12px] text-[var(--twr-warm-white)]/50">
          <p>© {new Date().getFullYear()} The Win Room. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="hover:text-[var(--twr-bronze)] transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-[var(--twr-bronze)] transition-colors">Terms</Link>
            <Link to="/careers" className="hover:text-[var(--twr-bronze)] transition-colors">Careers</Link>
            <Link to="/member/login" data-testid="footer-member-link" className="hover:text-[var(--twr-bronze)] transition-colors">Member Portal</Link>
            <Link to="/admin" data-testid="footer-admin-link" className="hover:text-[var(--twr-bronze)] transition-colors">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
